/**
 * Queue operations — high-level API on top of idb.js.
 *
 * Three operations exposed:
 *   • enqueueWrite({ method, url, body, headers, label }) — adds a write
 *     to the queue. Returns the queued record (which includes a UUID
 *     id used as the Idempotency-Key).
 *   • listPending()  — returns all records still queued (pending+syncing).
 *   • discardItem(id) — manual user override (lose the change).
 *
 * The queue is fed by `apiClient.js`'s axios error handler when a write
 * fails for "network reasons" (offline, 5xx, timeout). It does NOT
 * queue 4xx errors — those are permanent failures that need user
 * intervention.
 */
import { idbPut, idbGet, idbAll, idbDelete } from "./idb";

// Lightweight UUID v4 — avoids importing a library for one use.
const uuid = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );

/**
 * Add a failed write to the queue. Returns the queued record.
 *
 * @param {object} args
 * @param {"POST"|"PUT"|"PATCH"|"DELETE"} args.method
 * @param {string} args.url        — full URL (incl /api prefix)
 * @param {*}      args.body       — JSON-serialisable body
 * @param {object} args.headers    — copy of original request headers
 * @param {string} args.label      — human-friendly description
 */
export const enqueueWrite = async ({ method, url, body, headers = {}, label = "" }) => {
  const id = uuid();
  const record = {
    id,
    method,
    url,
    body,
    // Stash auth + idempotency headers verbatim so the drain can replay
    // exactly what the original axios request would have sent.
    headers: { ...headers, "Idempotency-Key": id },
    enqueued_at:      new Date().toISOString(),
    attempts:         0,
    status:           "pending",
    last_error:       null,
    last_attempt_at:  null,
    label,
  };
  await idbPut(record);
  notifyQueueChanged();
  return record;
};

export const listPending = () => idbAll();

export const discardItem = async (id) => {
  await idbDelete(id);
  notifyQueueChanged();
};

export const markSyncing = async (id) => {
  const record = await idbGet(id);
  if (!record) return null;
  record.status = "syncing";
  record.attempts = (record.attempts || 0) + 1;
  record.last_attempt_at = new Date().toISOString();
  await idbPut(record);
  return record;
};

export const markSynced = async (id) => {
  // Synced records get deleted — no point keeping them around.
  await idbDelete(id);
  notifyQueueChanged();
};

export const markPending = async (id, error) => {
  const record = await idbGet(id);
  if (!record) return;
  record.status = "pending";
  record.last_error = error || null;
  await idbPut(record);
  notifyQueueChanged();
};

export const markFailed = async (id, error) => {
  // 4xx errors — replaying won't help. Stays in store for manual retry.
  const record = await idbGet(id);
  if (!record) return;
  record.status = "failed";
  record.last_error = error || null;
  await idbPut(record);
  notifyQueueChanged();
};

// ────────────────────────────────────────────────────────────────────
// Pub-sub so the status chip updates without polling.
// ────────────────────────────────────────────────────────────────────
const listeners = new Set();
export const subscribeQueueChanged = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const notifyQueueChanged = () => {
  // Fire-and-forget; if a listener throws we don't want to take the
  // others down with it.
  for (const fn of listeners) {
    try { fn(); } catch { /* ignore listener errors */ }
  }
};
